import { ApiParams, prepareBaseListApiParams } from '@/api/helpers/apiHelpers';
import { QueryParamsType } from '@/api/helpers/apiParamsHelpers';
import { prepareResponse, throwResponseError, withPreparedResponse } from '@/helpers/apiHelpers/responseHelpers';
import { IDEMPOTENCY_KEY, idempotencyKeyHandler } from '@/helpers/apiHelpers/requestHelpers';
import { getVendorVaultCatalogApi } from '@/api/v1/organization/vendor-vault/getVendorVaultCatalogApi';
import {
  getRecipientRequestCountsApi,
  getRecipientRequestsApi,
} from '@/api/v1/organization/vendor-vault/getRecipientRequestsApi';
import { getRecipientRequestApi } from '@/api/v1/organization/vendor-vault/getRecipientRequestApi';
import {
  createRecipientRequestApi,
  CreateRecipientRequestPayloadType,
} from '@/api/v1/organization/vendor-vault/createRecipientRequestApi';
import {
  recipientRequestActionApi,
  RecipientRequestActionType,
} from '@/api/v1/organization/vendor-vault/recipientRequestActionApi';
import {
  recipientRequestStageApi,
  RecipientRequestStageActionType,
} from '@/api/v1/organization/vendor-vault/recipientRequestStageApi';
import {
  sendWireConfirmationApi,
  WireConfirmationPayloadType,
} from '@/api/v1/organization/vendor-vault/sendWireConfirmationApi';
import { getRecipientRequestVerificationApi } from '@/api/v1/organization/vendor-vault/getRecipientRequestVerificationApi';

export const VENDOR_VAULT_QUERY_KEYS = {
  catalog: ['vendor_vault_catalog'],
  counts: ['vendor_vault_counts'],
  requests: 'vendor_vault_requests',
  request: 'vendor_vault_request',
  verification: 'vendor_vault_verification',
} as const;

export const loadVendorVaultCatalog = () => withPreparedResponse(getVendorVaultCatalogApi);
export const loadRequestCounts = () => withPreparedResponse(getRecipientRequestCountsApi);
export const loadRecipientRequest = (id: string, reveal = false) =>
  withPreparedResponse(() => getRecipientRequestApi(id, reveal));
export const loadRequestVerification = (id: string) =>
  withPreparedResponse(() => getRecipientRequestVerificationApi(id));
export const runRequestAction = (id: string, action: RecipientRequestActionType) =>
  withPreparedResponse(() => recipientRequestActionApi(id, action));
export const runRequestStage = (id: string, stage: RecipientRequestStageActionType) =>
  withPreparedResponse(() => recipientRequestStageApi(id, stage));
export const sendWireConfirmation = (id: string, payload: WireConfirmationPayloadType) =>
  withPreparedResponse(() => sendWireConfirmationApi(id, payload));

export const loadRecipientRequests = (
  queryParams: QueryParamsType & { request_type?: string; archived?: string; q?: string }
) => {
  const params: ApiParams = prepareBaseListApiParams(queryParams);

  ['request_type', 'archived', 'q'].forEach((key) => {
    if (queryParams?.[key]) params[key] = queryParams[key];
  });

  return withPreparedResponse(() => getRecipientRequestsApi(params));
};

export const createRecipientRequest = async (payload: CreateRecipientRequestPayloadType) => {
  const { header, clearKey } = idempotencyKeyHandler(IDEMPOTENCY_KEY.CREATE_RECIPIENT_REQUEST);

  try {
    const response = await createRecipientRequestApi(payload, header);

    clearKey();

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};
